const CONTROL_ENV = "BBGM_SCREENSHOT_ALLOW_SERVER_CONTROLS";
const FIXTURE_ENV = "BBGM_SCREENSHOT_FIXTURE_ID";
const RESOURCE_PATH = "/gen/real-player-data.json";
const MAX_DELAY_MS = 30_000;
const ARM_TTL_MS = 60_000;

type ControlStatus = "armed" | "delaying" | "completed";

type OfficialDataDelayState = {
  armedAt: number;
  completedAt?: number;
  consumedCount: number;
  delayMs: number;
  delayPromise?: Promise<void>;
  expiresAt: number;
  fixtureId: string;
  joinedCount: number;
  requestId: string;
  resourcePath: typeof RESOURCE_PATH;
  status: ControlStatus;
};

export class ScreenshotScenarioControlError extends Error {
  status: number;

  constructor(message: string, status = 400) {
    super(message);
    this.name = "ScreenshotScenarioControlError";
    this.status = status;
  }
}

let state: OfficialDataDelayState | undefined;

const assertExactKeys = (value: Record<string, unknown>, keys: string[], label: string) => {
  const allowed = new Set(keys);
  const unknown = Object.keys(value).filter((key) => !allowed.has(key));
  if (unknown.length > 0) {
    throw new ScreenshotScenarioControlError(
      `${label} has unknown properties: ${unknown.join(", ")}`,
    );
  }
};

const requiredString = (value: unknown, label: string) => {
  if (typeof value !== "string" || value.trim().length === 0) {
    throw new ScreenshotScenarioControlError(`${label} must be a non-empty string`);
  }
  return value;
};

const assertEnabledFixture = (fixtureId: unknown) => {
  if (process.env[CONTROL_ENV] !== "1") {
    throw new ScreenshotScenarioControlError("screenshot server controls are disabled", 404);
  }
  const expectedFixtureId = process.env[FIXTURE_ENV];
  if (!expectedFixtureId) {
    throw new ScreenshotScenarioControlError(
      "screenshot server controls require BBGM_SCREENSHOT_FIXTURE_ID",
      404,
    );
  }
  if (requiredString(fixtureId, "fixtureId") !== expectedFixtureId) {
    throw new ScreenshotScenarioControlError("screenshot fixture identity mismatch", 403);
  }
  return expectedFixtureId;
};

const publicState = (current: OfficialDataDelayState) => ({
  consumedCount: current.consumedCount,
  delayMs: current.delayMs,
  fixtureId: current.fixtureId,
  joinedCount: current.joinedCount,
  requestId: current.requestId,
  resourcePath: current.resourcePath,
  status: current.status,
});

const requireMatchingState = (fixtureId: unknown, requestId: unknown) => {
  const expectedFixtureId = assertEnabledFixture(fixtureId);
  const expectedRequestId = requiredString(requestId, "requestId");
  if (state && state.expiresAt <= Date.now()) {
    state = undefined;
  }
  if (!state || state.fixtureId !== expectedFixtureId || state.requestId !== expectedRequestId) {
    throw new ScreenshotScenarioControlError("official-data delay arm was not found", 404);
  }
  return state;
};

export const armOfficialDataDelay = (input: unknown) => {
  if (!input || typeof input !== "object" || Array.isArray(input)) {
    throw new ScreenshotScenarioControlError("official-data delay arm must be an object");
  }
  const value = input as Record<string, unknown>;
  assertExactKeys(
    value,
    ["delayMs", "fixtureId", "requestId", "resourcePath"],
    "official-data delay arm",
  );
  const fixtureId = assertEnabledFixture(value.fixtureId);
  const requestId = requiredString(value.requestId, "requestId");
  if (
    !Number.isInteger(value.delayMs) ||
    Number(value.delayMs) <= 0 ||
    Number(value.delayMs) > MAX_DELAY_MS
  ) {
    throw new ScreenshotScenarioControlError(
      `delayMs must be an integer from 1 to ${MAX_DELAY_MS}`,
    );
  }
  if (value.resourcePath !== RESOURCE_PATH) {
    throw new ScreenshotScenarioControlError(
      `resourcePath must be the exact official data path ${RESOURCE_PATH}`,
    );
  }
  if (state && state.expiresAt > Date.now()) {
    throw new ScreenshotScenarioControlError("an official-data delay is already armed", 409);
  }
  const now = Date.now();
  state = {
    armedAt: now,
    consumedCount: 0,
    delayMs: Number(value.delayMs),
    expiresAt: now + ARM_TTL_MS,
    fixtureId,
    joinedCount: 0,
    requestId,
    resourcePath: RESOURCE_PATH,
    status: "armed",
  };
  return publicState(state);
};

export const getOfficialDataDelayStatus = (input: unknown) => {
  if (!input || typeof input !== "object" || Array.isArray(input)) {
    throw new ScreenshotScenarioControlError("official-data delay status must be an object");
  }
  const value = input as Record<string, unknown>;
  assertExactKeys(value, ["fixtureId", "requestId"], "official-data delay status");
  return publicState(requireMatchingState(value.fixtureId, value.requestId));
};

export const clearOfficialDataDelay = (input: unknown) => {
  if (!input || typeof input !== "object" || Array.isArray(input)) {
    throw new ScreenshotScenarioControlError("official-data delay clear must be an object");
  }
  const value = input as Record<string, unknown>;
  assertExactKeys(value, ["fixtureId", "requestId"], "official-data delay clear");
  const current = requireMatchingState(value.fixtureId, value.requestId);
  const result = publicState(current);
  state = undefined;
  return result;
};

export const waitForOfficialDataDelay = async (resourcePath: string) => {
  const current = state;
  if (!current || resourcePath !== RESOURCE_PATH) {
    return;
  }
  if (current.expiresAt <= Date.now()) {
    state = undefined;
    return;
  }
  if (current.status === "completed") {
    return;
  }
  if (current.status === "delaying") {
    current.joinedCount += 1;
    await current.delayPromise;
    return;
  }

  current.status = "delaying";
  current.consumedCount = 1;
  current.delayPromise = new Promise((resolve) => {
    setTimeout(resolve, current.delayMs);
  });
  await current.delayPromise;
  current.completedAt = Date.now();
  current.status = "completed";
};
